import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { userRequest } from "../api/requestMethods";

/**
 * useCodPolicy — the live platform Cash-on-Delivery ceiling.
 *
 * The server refuses COD for any order containing a line whose UNIT price is
 * above this figure (productPaymentPolicyService promotes it to ANY_ONLINE and
 * answers PAYMENT_METHOD_NOT_ALLOWED). The payment screens need the same number
 * so the refusal happens where the choice is made, not two screens later.
 *
 * Returns `null` until a live value has been read, and stays `null` if the read
 * fails. Callers must treat null as "do not apply the ceiling". That is the
 * whole point of not shipping a constant: the threshold is admin-editable at
 * runtime, so a stale guess could hide Cash on delivery from an order that
 * actually qualifies for it. Failing to read it simply restores the previous
 * behaviour, with the server still the backstop.
 *
 * Cached at module scope: the value changes about never, and four payment
 * screens plus their remounts should not each cost a request.
 *
 * /api/cod-policy is behind `auth`, so the read is skipped entirely while
 * nobody is signed in rather than firing a 401 nobody acts on. A signed-out
 * shopper therefore sees the pre-existing behaviour, which is the same thing a
 * failed read gives them.
 *
 * @returns {{ maxProductAmount: number|null }}
 */

let cachedAmount = null;
let inFlight = null;

const loadCodPolicy = () => {
  if (cachedAmount != null) return Promise.resolve(cachedAmount);
  if (!inFlight) {
    inFlight = userRequest
      .get("/api/cod-policy")
      .then((res) => {
        const n = Number(res?.data?.maxProductAmount);
        cachedAmount = Number.isFinite(n) && n > 0 ? n : null;
        return cachedAmount;
      })
      .catch(() => null)
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
};

export default function useCodPolicy() {
  const [maxProductAmount, setMaxProductAmount] = useState(cachedAmount);
  const currentUser = useSelector((state) => state.user?.currentUser);
  const isSignedIn = Boolean(currentUser);

  useEffect(() => {
    if (!isSignedIn) return undefined;
    let alive = true;
    loadCodPolicy().then((amount) => {
      if (alive && amount != null) setMaxProductAmount(amount);
    });
    return () => {
      alive = false;
    };
  }, [isSignedIn]);

  return { maxProductAmount };
}
